import { Classes } from "@blueprintjs/core";
import { observer } from "mobx-react";
import { Search } from "modules/common/components/form/Search";
import { LocalLoader } from "modules/common/components/loader/Loader";
import { CollapseExpandAll } from "modules/common/components/table/CollapseExpandAll";
import { NoData } from "modules/common/components/table/NoData";
import { PlanrTooltip } from "modules/common/components/tooltip/PlanrTooltip";
import { Zoomer } from "modules/common/components/zoomer/Zoomer";
import { DefaultSelectedOption, renderSingleOption, SimpleSelect } from "modules/common/services/form/select";
import { menuItems } from "modules/root/components/menu/MainMenu";
import { PlanDayHeader } from "modules/spending/workload-plan/components/PlanDayHeader";
import { PeriodRangeSlider } from "modules/spending/workload-plan/components/PeriodRangeSlider";
import { PlanScale, slotRange } from "modules/spending/workload-plan/models/plan-scale";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useProjectPlanGrid } from "../hooks/useProjectPlanGrid";
import { useVisibleProjects } from "../hooks/useVisibleProjects";
import { buildProjectPlan, ProjectPlanResponse } from "../models/project-plan-builder";
import { MONTHS_LIMITS, ProjectPlanStoreType } from "../models/project-plan-store";
import { EMPTY_PROJECT_PLAN, ProjectPlanSnapshot } from "../models/project-plan-types";
import { ProjectSection } from "./ProjectSection";
import styles from "./ProjectPlanPage.module.scss";

const menu = menuItems();

/** Масштаб сетки */
const SCALE_OPTIONS: SelectItem[] = [
    { id: "day", label: "По дням" },
    { id: "week", label: "По неделям" },
];

/** Глубина показа: свой набор вариантов для каждого масштаба */
const MONTHS_OPTIONS: { [key in PlanScale]: SelectItem[] } = { day: [], week: [] };

(Object.keys(MONTHS_OPTIONS) as PlanScale[]).forEach((scale) => {
    const limits = MONTHS_LIMITS[scale];

    for (let months = limits.min; months <= limits.max; months++) {
        MONTHS_OPTIONS[scale].push({ id: String(months), label: `${months} мес.` });
    }
});

interface ProjectPlanPageProps {
    store: ProjectPlanStoreType;
}

/**
 * «Плановый график работ»: проекты слева, недели месяцев справа.
 * Страница только показывает — редактирование плана живёт в Табеле Плане.
 */
export const ProjectPlanPage = observer((props: ProjectPlanPageProps) => {
    const { store } = props;

    const [plan, setPlan] = useState<ProjectPlanSnapshot>(EMPTY_PROJECT_PLAN);
    const [loaded, setLoaded] = useState(false);
    const [selectedSlot, setSelectedSlot] = useState<number | null>(null);

    const gridRef = useRef<HTMLDivElement>(null);

    const load = useCallback(
        async (year?: number, month?: number) => {
            const response: ProjectPlanResponse | null = await store.fetch(year, month);

            if (response) {
                const next = buildProjectPlan(response, store.monthsOnPage);
                setPlan(next);

                // Кнопки «свернуть/развернуть всё» перебирают уже известные ключи
                // (SectionCollapser.setAll), поэтому проект должен попасть в карту
                // до первого клика по нему — иначе кнопки не делают ничего.
                // Уже открытые проекты не трогаем: смена периода не должна их схлопывать.
                next.projects.forEach((project) => {
                    if (!store.collapser.opened.has(project.id)) {
                        store.collapser.set(project.id, false);
                    }
                });
            }

            setLoaded(true);
        },
        [store]
    );

    useEffect(() => {
        document.title = menu.schedules.nested.projectPlan.label;
    }, []);

    useEffect(() => {
        load();
        // грузим один раз на монтирование; период и глубина перезагружают сами
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const gotoMonth = useCallback(
        (year: number, month: number) => {
            store.setPeriod(year, month);
            setSelectedSlot(null);
            load(year, month);
        },
        [store, load]
    );

    const changeScale = useCallback(
        (scale: PlanScale) => {
            store.setScale(scale);
            setSelectedSlot(null);

            // данных на новый масштаб хватает — сетку пересоберёт useProjectPlanGrid
            if (store.needsReload) {
                load();
            }
        },
        [store, load]
    );

    const changeMonthsOnPage = useCallback(
        (months: number) => {
            store.setMonthsOnPage(months);
            setSelectedSlot(null);

            if (store.needsReload) {
                load();
            }
        },
        [store, load]
    );

    const { grid, periods, monthBreaks, columnsCount } = useProjectPlanGrid(
        plan,
        store.scale,
        store.monthsOnPage
    );

    const projects = useVisibleProjects(plan.projects, store.pureQuery, columnsCount);

    const onSelectSlot = useCallback(
        (slot: number) => setSelectedSlot((current) => (current === slot ? null : slot)),
        []
    );

    /** Выделенный столбец в координатах колонок: в недельном масштабе это вся неделя */
    const selectedRange = useMemo(() => {
        if (selectedSlot === null) {
            return null;
        }

        const slot = grid.slots[selectedSlot];

        return slot && !slot.isEmpty ? slotRange(grid, slot.start) : null;
    }, [selectedSlot, grid]);

    const monthsOptions = MONTHS_OPTIONS[store.scale];

    const monthsOption = useMemo(
        () => monthsOptions.find((item) => item.id === String(store.monthsOnPage)) || monthsOptions[0],
        [monthsOptions, store.monthsOnPage]
    );

    const scaleOption = useMemo(
        () => SCALE_OPTIONS.find((item) => item.id === store.scale) || SCALE_OPTIONS[0],
        [store.scale]
    );

    return (
        <div className={styles.page}>
            <LocalLoader active={store.isLoading || !loaded} />

            <div className={styles.toolbar}>
                <div className={styles.view}>
                    <div className={styles.selects}>
                        <div className={styles.scale} title="Масштаб сетки: дни или недели">
                            <SimpleSelect
                                className={`full-width-select ${Classes.FILL}`}
                                filterable={false}
                                activeItem={scaleOption}
                                items={SCALE_OPTIONS}
                                itemRenderer={renderSingleOption}
                                onItemSelect={(item: SelectItem) => changeScale(item.id as PlanScale)}
                            >
                                <DefaultSelectedOption option={scaleOption} />
                            </SimpleSelect>
                        </div>

                        <div className={styles.months} title="Сколько месяцев показывать в этом масштабе">
                            <SimpleSelect
                                className={`full-width-select ${Classes.FILL}`}
                                filterable={false}
                                activeItem={monthsOption}
                                items={monthsOptions}
                                itemRenderer={renderSingleOption}
                                onItemSelect={(item: SelectItem) => changeMonthsOnPage(+item.id)}
                            >
                                <DefaultSelectedOption option={monthsOption} />
                            </SimpleSelect>
                        </div>
                    </div>

                    <div className={styles.hint}>
                        Проекты, по которым есть работы от сегодня: собственные силы — по плановому
                        табелю, подрядчики — по договорам из состава затрат
                    </div>
                </div>

                <div className={styles.period}>
                    <PeriodRangeSlider
                        year={store.year}
                        month={store.month}
                        monthsCount={store.monthsOnPage}
                        onChange={gotoMonth}
                    />

                    <div className={styles.norms}>
                        <span className={styles.norm}>
                            Проектов: <b>{projects.length}</b>
                        </span>
                    </div>
                </div>

                <div className={styles.actions}>
                    <Search query={store.query} onSearch={store.setQuery} />
                    <Zoomer zoom={store.zoom} min={50} max={100} step={5} onChange={store.setZoom} />
                    <CollapseExpandAll onChange={store.collapser.setAll} />
                </div>
            </div>

            <div className={styles.scroll}>
                {/*
                    Зум сетки — через CSS zoom, а не transform: scale. Transform заводит
                    свой контекст координат: липкая шапка дней внутри него перестаёт липнуть
                    на любом масштабе, кроме 100%, и уезжает вверх вместе со строками.
                    zoom масштабирует саму раскладку, поэтому sticky работает как обычно,
                    а ширина прокрутки совпадает с видимой сеткой (при scale контейнер
                    оставался прежней ширины, и справа висела пустота). Жесты не страдают:
                    все замеры идут через getBoundingClientRect (trackScale / columnAt).
                */}
                <div className={styles.grid} ref={gridRef} style={{ zoom: store.transform }}>
                    <PlanDayHeader
                        periods={periods}
                        grid={grid}
                        selectedSlot={selectedSlot}
                        onSelectSlot={onSelectSlot}
                    />

                    {projects.map((project) => (
                        <ProjectSection
                            key={project.id}
                            project={project}
                            grid={grid}
                            monthBreaks={monthBreaks}
                            selectedSlot={selectedRange}
                            collapsed={!store.collapser.opened.get(project.id)}
                            onToggle={store.collapser.toggle}
                        />
                    ))}
                </div>
            </div>

            <PlanrTooltip containerRef={gridRef} />

            {loaded && !store.isLoading && !projects.length && <NoData className={styles.nodata} />}
        </div>
    );
});
